// Feed redesign — «К разбору» popover (variant B).
//
// Drops DOWN from under the «к разбору» counter (SummaryCells owns the
// open state, anchor and outside-click / Esc handling; this component is
// the presentational popover only). A small arrow points up at the
// counter. Width is content-driven, not full-screen.
//
// Content (variant B — numbers from computeReviewBreakdown, the same
// source the counter uses, so the header always equals the counter):
//   В СЧЁТЕ (actionable):
//     · не вычислено  = service_failed
//     · прочее        = other (strictly — NOT other+uncategorised)
//     header «к разбору» = service_failed + other
//   СПРАВОЧНО · НЕ В СЧЁТЕ (muted, excluded from the count):
//     · файл недоступен = file_unavailable
//     · нет эталона     = no_reference
//     · не применимо    = not_applicable
//     · без категории   = failed_uncat (old FAILED, will be classified)

import { forwardRef } from 'react';

import type {
  FailureBreakdownResponse,
  FailureCategoryR,
} from '../api/types';
import { computeReviewBreakdown } from '../lib/reviewBreakdown';
import './BreakdownSheet.css';

interface Row {
  key: string;
  label: string;
  count: number;
  note?: string | null;
  /** The R-bucket this row drills into, or ``null`` for rows with no
   *  real ``failure_category`` value (``failed_uncat`` — old FAILED rows
   *  not yet classified; nothing to filter on). */
  drill: FailureCategoryR | null;
}

export const BreakdownSheet = forwardRef<
  HTMLDivElement,
  {
    breakdown: FailureBreakdownResponse | undefined;
    open: boolean;
    /** Drill the feed into the given bucket(s). When absent the rows
     *  render as plain (non-clickable) summary lines. */
    onDrill?: (categories: FailureCategoryR[]) => void;
  }
>(function BreakdownSheet({ breakdown, open, onDrill }, ref) {
  if (!open) return null;

  const r = computeReviewBreakdown(breakdown);
  const loading = breakdown == null;
  const fmt = (n: number) => (loading ? '—' : String(n));

  // «В счёте» — the two actionable buckets that sum to the counter.
  const inCount: Row[] = [
    { key: 'service_failed', label: 'не вычислено', count: r.serviceFailed, drill: 'service_failed' },
    { key: 'other', label: 'прочее', count: r.other, drill: 'other' },
  ];
  // «Справочно · не в счёте» — excluded from the count.
  const reference: Row[] = [
    { key: 'file_unavailable', label: 'файл недоступен', count: r.fileUnavailable, note: null, drill: 'file_unavailable' },
    { key: 'no_reference', label: 'нет эталона', count: r.noReference, note: null, drill: 'no_reference' },
    { key: 'not_applicable', label: 'не применимо', count: r.notApplicable, note: null, drill: 'not_applicable' },
    {
      key: 'failed_uncat',
      label: 'без категории',
      count: r.failedUncat,
      note: 'старые, будут классифицированы',
      drill: null,
    },
  ];

  // A row is a drill button when a handler exists AND the row maps to a
  // real ``failure_category`` value. Loading rows are inert.
  const renderRow = (row: Row) => {
    const drillable = !loading && onDrill != null && row.drill != null;
    const inner = (
      <>
        <span className="row-label">
          {row.label}
          {row.note && <span className="row-note"> · {row.note}</span>}
        </span>
        <span className="row-count">{fmt(row.count)}</span>
      </>
    );
    if (!drillable) {
      return (
        <li key={row.key} className="pop-row" data-testid={`row-${row.key}`}>
          {inner}
        </li>
      );
    }
    return (
      <li key={row.key} className="pop-row" data-testid={`row-${row.key}`}>
        <button
          type="button"
          className="pop-row-btn"
          onClick={() => onDrill([row.drill as FailureCategoryR])}
          aria-label={`показать «${row.label}»`}
        >
          {inner}
        </button>
      </li>
    );
  };

  return (
    <div
      ref={ref}
      className="breakdown-popover"
      role="dialog"
      aria-label="Разбор провалов"
      data-testid="breakdown-sheet"
    >
      <div className="pop-arrow" aria-hidden />
      <div className="pop-head">
        <span className="pop-title">к разбору</span>
        <span className="pop-total" data-testid="pop-total">
          {loading ? '—' : r.inCount}
        </span>
      </div>

      {/* Combined drill-in — «показать все N к разбору» = service_failed +
          other (matches the counter). Rendered only when there's a handler
          and a non-zero «в счёте» set. */}
      {!loading && onDrill != null && r.inCount > 0 && (
        <button
          type="button"
          className="pop-show-all"
          data-testid="pop-show-all"
          onClick={() => onDrill(['service_failed', 'other'])}
        >
          показать все {r.inCount} к разбору →
        </button>
      )}

      <ul className="pop-list">{inCount.map(renderRow)}</ul>

      <div className="pop-divider" />
      <div className="pop-subhead">справочно · не в счёте</div>

      <ul className="pop-list muted">{reference.map(renderRow)}</ul>
    </div>
  );
});
